import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Linkedin } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, Eyebrow, scrollToId } from "@/lib/motion";

const BADGES = [
  { role: "Founder", org: "ZD Design" },
  { role: "Group Leader", org: "ACA Business Club" },
  { role: "Founder", org: "SEM San Diego" },
  { role: "Founder", org: "So Cal Marketing Club" },
];

export default function ToddPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goBook = (e) => {
    if (e) e.preventDefault();
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("book"), 600);
    setTimeout(() => scrollToId("book"), 1600);
    } else {
      scrollToId("book");
    }
  };

  return (
    <main data-testid="todd-page">
      <Seo
        title="Todd Meisler — Founder & SEO Strategist, ZD Design"
        description="Meet Todd Meisler, founder of ZD Design — 30+ years in search, founder of SEM San Diego, and your one-on-one strategist. Book a free strategy call."
        path="/todd"
      />

      <section className="grain relative overflow-hidden pb-16 pt-40 md:pb-24 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
            <div>
              <FadeUp>
                <Eyebrow>The founder</Eyebrow>
                <h1 className="mt-5 font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
                  Todd Meisler<span className="text-flame">.</span>
                </h1>
                <div className="mt-4 font-mono text-base uppercase tracking-[0.22em] text-flame">
                  Founder & Chief Strategist
                </div>
                <p className="mt-7 max-w-xl text-lg leading-relaxed text-mist md:text-xl">
                  Industry-recognized SEO specialist and the founder of ZD
                  Design. For more than 30 years, Todd has helped brands lead
                  with purpose and human-centric marketing — and he's still the
                  strategist every client works with directly.
                </p>
              </FadeUp>
              <FadeUp delay={0.12}>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <button
                    data-testid="todd-hero-book-button"
                    onClick={goBook}
                    className="group flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
                  >
                    Book a free strategy call
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                  <a
                    href="https://www.linkedin.com/in/zddesign/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="todd-linkedin-link"
                    className="group flex items-center gap-2 rounded-2xl border border-slate-300 px-6 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-slate-900 transition-all duration-300 hover:border-flame/60 hover:text-flame"
                  >
                    <Linkedin className="h-4 w-4 text-flame" />
                    Connect on LinkedIn
                  </a>
                </div>
              </FadeUp>
            </div>

            <FadeUp delay={0.15}>
              <div
                data-testid="todd-portrait-frame"
                className="group relative overflow-hidden rounded-2xl border border-slate-200"
              >
                <img
                  src="/img/todd-brick-wall.webp"
                  alt="Todd Meisler, Founder of ZD Design Agency"
                  className="aspect-[4/5] w-full scale-105 object-cover object-top grayscale transition-all duration-700 group-hover:scale-100 group-hover:grayscale-0"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                <div className="glass absolute bottom-5 left-5 rounded-xl border border-slate-200 px-5 py-4">
                  <div className="font-display text-xl font-bold text-slate-900">
                    Todd Meisler
                  </div>
                  <div className="font-mono text-base uppercase tracking-[0.22em] text-flame">
                    Founder & Lead Strategist
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
            <FadeUp>
              <Eyebrow>A career spent teaching search</Eyebrow>
              <h2 className="mt-5 font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                He doesn't just run campaigns.{" "}
                <span className="text-flame">He teaches search.</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-mist">
                Todd has spent his career teaching search — founding SEM San
                Diego and So Cal Marketing Club, speaking at conferences and
                group events, and leading the ACA Business Club group.
              </p>
              <p className="mt-5 text-lg leading-relaxed text-mist">
                His focus is home-service companies and small business owners:
                clear strategy, honest reporting, and websites that earn their
                keep. No account managers, no juniors — every client works
                one-on-one with Todd to build and execute their marketing plan.
              </p>
            </FadeUp>
            <FadeUp delay={0.12}>
              <div className="grid grid-cols-1 gap-3 self-center sm:grid-cols-2">
                {BADGES.map((b) => (
                  <div
                    key={b.org}
                    data-testid={`todd-badge-${b.org.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center gap-4 rounded-xl border border-slate-200 bg-coal/70 px-5 py-4 transition-colors duration-300 hover:border-flame/40"
                  >
                    <span className="h-2 w-2 shrink-0 rounded-full bg-flame" />
                    <div>
                      <div className="font-mono text-[13px] uppercase tracking-[0.2em] text-faint">
                        {b.role}
                      </div>
                      <div className="font-semibold text-slate-900">{b.org}</div>
                    </div>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
            <FadeUp>
              <Eyebrow>Beyond the desk</Eyebrow>
              <h2 className="mt-5 font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                An artist at heart.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-mist">
                Beyond marketing, Todd is a singer, songwriter, musician, and
                painter — dedicated to creativity in all its forms. It shows up
                in his work: the instinct to make a brand not just heard, but
                truly felt.
              </p>
              <blockquote className="mt-8 border-l-2 border-flame/50 pl-5 text-lg italic leading-relaxed text-slate-700">
                "My passion lies in simplifying the complex and crafting
                strategies that make your voice not just heard — but truly
                felt."
                <span className="mt-2 block font-mono text-base not-italic text-faint">
                  — Todd, Chief Strategist
                </span>
              </blockquote>
            </FadeUp>
            <FadeUp delay={0.12}>
              <div className="glass self-center rounded-2xl border border-slate-200 p-10 text-center md:p-14">
                <h2 className="font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                  Talk strategy with Todd himself.
                </h2>
                <p className="mx-auto mt-5 max-w-md text-lg leading-relaxed text-mist">
                  Pick a time that suits you — the call is free, and there's no
                  pitch you didn't ask for.
                </p>
                <button
                  data-testid="todd-cta-book"
                  onClick={goBook}
                  className="group mx-auto mt-8 flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
                >
                  Book a free strategy call
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <Link
                  to="/about"
                  data-testid="todd-about-link"
                  className="mt-6 inline-block font-mono text-base uppercase tracking-[0.2em] text-faint transition-colors hover:text-flame"
                >
                  Read the studio story
                </Link>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
}
